import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import logoLight from '../assets/xtract-symbol-light.png'
import logoDark from '../assets/xtract-symbol-dark.png'

/**
 * Public Privacy Policy page - accessible without authentication.
 * Linked from: Login page, Layout sidebar footer, Marketplace listing, Partner Center.
 */
function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="px-8 lg:px-16 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoLight} alt="Xtract" className="w-8 h-8 rounded-lg dark:hidden" />
            <img src={logoDark} alt="Xtract" className="hidden dark:block w-8 h-8 rounded-lg" />
            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Xtract</span>
            <span className="text-xs text-gray-400 dark:text-gray-500">by Synapx AI</span>
          </div>
          <Link
            to="/login"
            className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="px-8 lg:px-16 py-12">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
          Last updated: July 21, 2026
        </p>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 space-y-8 text-gray-700 dark:text-gray-300 leading-relaxed">
          
          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">1. Introduction</h2>
            <p>
              Xtract (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;), operated by Synapx Ltd, provides an AI-powered
              document extraction and processing platform available through the Microsoft Azure
              Marketplace. This Privacy Policy explains how we collect, use, disclose, and safeguard
              your information when you use our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">2. Information We Collect</h2>
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">Account Information</h3>
            <p className="mb-3">
              When you sign in via Microsoft Entra ID (Azure Active Directory), we receive your name,
              email address, organisation name, and tenant ID. We do not collect or store your
              Microsoft password.
            </p>
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">Documents and Data</h3>
            <p className="mb-3">
              Documents you upload for processing (PDFs) are
              stored in Azure Blob Storage within your own Azure subscription. Extracted data (fields, values,
              and metadata) is stored in an Azure SQL Database within your own subscription,
              isolated from all other customers.
            </p>
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">Usage Data</h3>
            <p>
              We collect usage metrics including pages processed, fields extracted, and processing
              timestamps. This data is used for metered billing through the Azure Marketplace and
              to improve our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-2">
              <li>To provide and maintain the document extraction service</li>
              <li>To authenticate your identity via Microsoft Entra ID</li>
              <li>To process documents using Azure AI Content Understanding and Azure OpenAI</li>
              <li>To calculate and report metered usage for billing through the Azure Marketplace</li>
              <li>To send service notifications and respond to support requests</li>
              <li>To improve the accuracy and performance of our AI models</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">4. Data Processing and AI Services</h2>
            <p className="mb-3">
              Your documents are processed using the following Microsoft Azure services:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Azure AI Content Understanding</strong> - for document content extraction and analysis</li>
              <li><strong>Azure OpenAI Service</strong> - for field normalisation and data structuring</li>
              <li><strong>Azure Blob Storage</strong> - for temporary document storage during processing</li>
              <li><strong>Azure SQL Database</strong> - for storing extracted data and usage records</li>
            </ul>
            <p className="mt-3">
              Document content sent to AI services is processed in accordance with
              Microsoft&apos;s <a href="https://www.microsoft.com/licensing/terms/productoffering/MicrosoftAzure" target="_blank" rel="noopener noreferrer" className="text-navy-700 dark:text-navy-400 underline">Azure service terms</a> and
              is not used to train foundation models.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">5. Data Retention</h2>
            <p>
              Document retention is configurable through the Xtract application. Administrators
              can set retention policies per document type, after which documents are moved to
              a recycle bin and eventually permanently deleted. Extracted data and usage records
              are retained for the period required for billing and audit purposes (typically 12
              months after subscription termination). You may request deletion of your data at
              any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">6. Data Security</h2>
            <p>
              We implement industry-standard security measures including encryption in transit
              (TLS 1.2+), encryption at rest (Azure Storage Service Encryption), role-based access
              control, and network isolation. All data is hosted in Microsoft Azure data centres in
              your selected deployment region. Each customer deployment is fully isolated with
              dedicated database, storage, and compute resources.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">7. Data Sharing</h2>
            <p>
              We do not sell, rent, or trade your personal data or document content to third parties.
              Data is shared only with Microsoft Azure services as required to deliver the service,
              and with Microsoft for marketplace billing purposes. We may disclose information if
              required by law or to protect our rights.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">8. Your Rights</h2>
            <p className="mb-3">Under applicable data protection laws (including UK GDPR), you have the right to:</p>
            <ul className="list-disc list-inside space-y-2">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Request portability of your data</li>
              <li>Withdraw consent at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">9. Cookies and Tracking</h2>
            <p>
              Xtract uses only essential session cookies required for authentication via Microsoft
              Entra ID. We do not use advertising cookies, analytics trackers, or third-party
              tracking technologies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material
              changes by posting the updated policy on this page with a revised &quot;Last updated&quot; date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or wish to exercise your data
              rights, please contact us at:
            </p>
            <div className="mt-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="font-medium text-gray-900 dark:text-gray-100">Synapx Ltd</p>
              <p>Email: <a href="mailto:info@synapx.com" className="text-navy-700 dark:text-navy-400 underline">info@synapx.com</a></p>
              <p>Website: <a href="https://synapx.com" target="_blank" rel="noopener noreferrer" className="text-navy-700 dark:text-navy-400 underline">synapx.com</a></p>
            </div>
          </section>

        </div>

        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-8">
          © {new Date().getFullYear()} Xtract by Synapx Ltd. All rights reserved.
        </p>
      </main>
    </div>
  )
}

export default PrivacyPolicy
